import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorBanner } from '../ui/ErrorBanner';
import { FilterTabs } from '../ui/FilterTabs';
import { Pagination } from '../ui/Pagination';
import { CustodyCopyHeader, copyBadgeLabel } from './CustodyCopyHeader';
import { isZipSource } from './zipPdf';
import { useValidationEvidencePage } from '../../digitize/useValidationEvidencePage';
import {
  VALIDATION_DEFAULT_PAGE_SIZE,
  VALIDATION_PAGE_SIZES,
  VALIDATION_STATUS_ORDER,
  countValidationStatuses,
  normalizeValidationStatus,
  splitValidationGroup,
  validationStatusClass,
  validationTabRank,
} from '../../digitize/validationPanelShared';

export { VALIDATION_PAGE_SIZES } from '../../digitize/validationPanelShared';

const DIGITIZE_TABS = [
  { value: 'classify', label: 'Classify' },
  { value: 'extract', label: 'Extract' },
  { value: 'review', label: 'Review' },
  { value: 'validation', label: 'Validation' },
];

const STEP_VALUES = new Set(DIGITIZE_TABS.map((tab) => tab.value));

function padThumbIndex(index) {
  return String(index + 1).padStart(2, '0');
}

function isMetaFactKey(key) {
  if (!key || typeof key !== 'string') return false;
  return key.startsWith('ABDP_') || key === 'SOURCE_FILE_NAME';
}

function isClassificationFactKey(key) {
  if (!key || typeof key !== 'string') return false;
  return (
    key === 'DOCUMENT_TYPE' ||
    key.startsWith('DOCUMENT_TYPE_') ||
    key.startsWith('PAGE_COUNT_') ||
    key.startsWith('PAGES_') ||
    key.startsWith('START_PAGE_') ||
    (key.startsWith('SOURCE_FILE_') && key !== 'SOURCE_FILE_NAME')
  );
}

const EVIDENCE_LINE = /^(.+?)\s+-\s+(.+?):\s*(.*)$/;
const SOURCE_JD = /^(.*?)\s*\((JD_[^)]+)\)\s*$/i;

/**
 * Parse Process rule detail text into a failure message plus evidence rows
 * grouped by source document. Lines look like
 * `Confirmation of Insurance (JD_1_4) - Province: BC`.
 */
function parseValidationDetail(detail) {
  const lines = String(detail || '')
    .split('\n')
    .map((line) => line.replace(/[\u200b-\u200d\ufeff]/g, '').trimEnd())
    .filter((line) => line.trim() !== '');
  const failureLines = [];
  const sources = new Map();
  let inFailure = false;
  for (const line of lines) {
    const evidence = EVIDENCE_LINE.exec(line);
    if (evidence && !/^Failure:/i.test(line)) {
      inFailure = false;
      const [, rawSource, field, value] = evidence;
      const jd = SOURCE_JD.exec(rawSource.trim());
      const source = jd ? jd[1].trim() || rawSource.trim() : rawSource.trim();
      const key = `${source}|${jd ? jd[2] : ''}`;
      if (!sources.has(key)) {
        sources.set(key, { source, jd: jd ? jd[2] : null, rows: [] });
      }
      sources.get(key).rows.push({ field: field.trim(), value: value.trim() });
      continue;
    }
    if (/^Failure:/i.test(line)) {
      inFailure = true;
      failureLines.push(line.replace(/^Failure:\s*/i, ''));
      continue;
    }
    if (inFailure || failureLines.length) {
      inFailure = true;
      failureLines.push(line);
    }
  }
  const failureSummary = failureLines[0] || '';
  const failureRest = failureLines.slice(1).join('\n').trim();
  return {
    failureSummary,
    failureRest,
    sources: Array.from(sources.values()),
  };
}

function ValidationFlag({ status }) {
  const tone =
    status === 'Yes'
      ? 'text-emerald-600'
      : status === 'No'
        ? 'text-rose-600'
        : status === 'Error'
          ? 'text-amber-600'
          : 'text-slate-400';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone}`}>
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
        <path d="M3 1.5a.75.75 0 0 1 .75.75V3h8.09a.75.75 0 0 1 .6 1.2L10.6 6.75l1.84 2.55a.75.75 0 0 1-.6 1.2H3.75v3.75a.75.75 0 0 1-1.5 0v-12A.75.75 0 0 1 3 1.5Z" />
      </svg>
      {status}
    </span>
  );
}

function ValidationStatusIcon({ status }) {
  const base = 'inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold';
  if (status === 'Yes') {
    return (
      <span className={`${base} bg-emerald-500 text-white`} aria-label="Passed">
        ✓
      </span>
    );
  }
  if (status === 'No') {
    return (
      <span className={`${base} bg-rose-500 text-white`} aria-label="Failed">
        ✕
      </span>
    );
  }
  if (status === 'Error') {
    return (
      <span className={`${base} bg-amber-500 text-white`} aria-label="Engine error">
        !
      </span>
    );
  }
  return (
    <span className={`${base} bg-slate-200 text-slate-600`} aria-label="Not applicable">
      –
    </span>
  );
}

function Chevron({ open }) {
  return (
    <span
      className={`inline-block w-3 text-center text-[10px] text-eorig-muted transition-transform ${
        open ? 'rotate-90' : ''
      }`}
      aria-hidden="true"
    >
      ▶
    </span>
  );
}

function ValidationRuleRow({ item, expanded, onToggle }) {
  const [showMore, setShowMore] = useState(false);
  const status = item.status;
  const parsed = useMemo(() => parseValidationDetail(item.detail), [item.detail]);
  const hasFailure = Boolean(parsed.failureSummary) && status !== 'Yes';
  const evidenceRows = parsed.sources.flatMap((source) =>
    source.rows.map((row) => ({
      key: `${source.source}|${source.jd || ''}|${row.field}`,
      source: source.source,
      jd: source.jd,
      field: row.field,
      value: row.value,
    }))
  );
  const hasBody = hasFailure || evidenceRows.length > 0 || Boolean(item.detail);
  const failureTone =
    status === 'Error'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-rose-200 bg-rose-50 text-rose-800';
  const isNa = (value) => /^(na|n\/a|none|null|-)$/i.test(value || '');

  return (
    <li className="border-t border-eorig-border/70">
      <div
        role={hasBody ? 'button' : undefined}
        tabIndex={hasBody ? 0 : undefined}
        onClick={hasBody ? onToggle : undefined}
        onKeyDown={
          hasBody
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        aria-expanded={hasBody ? expanded : undefined}
        className={`grid grid-cols-[1fr_120px_90px] items-center gap-3 px-4 py-2.5 sm:grid-cols-[1fr_140px_110px_90px] ${
          hasBody ? 'cursor-pointer hover:bg-eorig-surface/50' : ''
        }`}
      >
        <div className="flex min-w-0 items-center gap-2 pl-6">
          {hasBody ? <Chevron open={expanded} /> : <span className="w-3" />}
          <span className="truncate text-sm text-eorig-ink" title={item.label}>
            {item.label}
          </span>
        </div>
        <div>
          <ValidationFlag status={status} />
        </div>
        <div className="hidden sm:block">
          <ValidationStatusIcon status={status} />
        </div>
        <div className="text-right text-[11px] text-eorig-muted">
          {evidenceRows.length ? `${evidenceRows.length} field${evidenceRows.length === 1 ? '' : 's'}` : '—'}
        </div>
      </div>

      {expanded && hasBody ? (
        <div className="px-4 pb-3 pl-14">
          {hasFailure ? (
            <div className={`mb-2 rounded-lg border px-3 py-2 text-xs ${failureTone}`}>
              <p className="font-medium">{parsed.failureSummary}</p>
              {parsed.failureRest ? (
                <>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowMore((value) => !value);
                    }}
                    className="mt-1 text-[11px] font-semibold underline-offset-2 hover:underline"
                  >
                    {showMore ? 'Hide engine output' : 'Show engine output'}
                  </button>
                  {showMore ? (
                    <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed opacity-90">
                      {parsed.failureRest}
                    </pre>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          {evidenceRows.length ? (
            <div className="overflow-hidden rounded-lg bg-eorig-surface/60">
              {evidenceRows.map((row, index) => (
                <div
                  key={`${row.key}-${index}`}
                  className="grid grid-cols-[1fr_minmax(120px,40%)] items-baseline gap-4 px-3 py-2 text-xs odd:bg-eorig-surface/40"
                >
                  <span className="min-w-0 truncate text-eorig-ink" title={`${row.source}${row.jd ? ` (${row.jd})` : ''} - ${row.field}`}>
                    {row.source}
                    {row.jd ? <span className="text-eorig-muted"> ({row.jd})</span> : null}
                    <span className="text-eorig-muted"> - </span>
                    {row.field}
                  </span>
                  <span
                    className={`truncate font-medium ${isNa(row.value) ? 'text-eorig-muted/70' : 'text-eorig-ink'}`}
                    title={row.value}
                  >
                    {row.value || '—'}
                  </span>
                </div>
              ))}
            </div>
          ) : !hasFailure && item.detail ? (
            <pre className="whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-eorig-muted">
              {item.detail}
            </pre>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function ValidationPanel({
  items,
  assetId,
  fetchEvidencePage,
  fetchEvidenceDetails,
  evidenceReloadToken = 0,
  canMutateVa,
  vaBusy,
  onRunValidate,
  loanTape,
  onRunTapeValidate,
}) {
  const server = useValidationEvidencePage({
    assetId,
    fetchEvidencePage,
    fetchEvidenceDetails,
    reloadToken: evidenceReloadToken,
  });
  const [activeTab, setActiveTab] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [expandedRules, setExpandedRules] = useState(() => new Set());
  const [pageSize, setPageSize] = useState(VALIDATION_DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const autoCollapsedKey = useRef('');

  const normalized = useMemo(
    () =>
      (items || []).map((item) => {
        const { tab, group } = splitValidationGroup(item.groupName || item.group);
        return {
          ...item,
          tab,
          group,
          status: normalizeValidationStatus(item.resultStatus || (item.satisfied ? 'Yes' : 'No')),
        };
      }),
    [items]
  );

  const tabs = useMemo(() => {
    const seen = new Map();
    for (const item of normalized) {
      if (!seen.has(item.tab)) seen.set(item.tab, []);
      seen.get(item.tab).push(item);
    }
    return Array.from(seen.entries())
      .map(([name, tabItems]) => ({ name, items: tabItems, counts: countValidationStatuses(tabItems) }))
      .sort((a, b) => validationTabRank(a.name) - validationTabRank(b.name) || a.name.localeCompare(b.name));
  }, [normalized]);

  const currentTab = tabs.find((tab) => tab.name === activeTab) || tabs[0] || null;
  const tabItems = currentTab ? currentTab.items : [];
  const counts = useMemo(() => countValidationStatuses(tabItems), [tabItems]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tabItems.filter((item) => {
      if (statusFilter && item.status !== statusFilter) return false;
      if (!needle) return true;
      return (
        String(item.label || '').toLowerCase().includes(needle) ||
        String(item.group || '').toLowerCase().includes(needle) ||
        String(item.detail || '').toLowerCase().includes(needle)
      );
    });
  }, [tabItems, statusFilter, query]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const item of filtered) {
      if (!map.has(item.group)) map.set(item.group, []);
      map.get(item.group).push(item);
    }
    return Array.from(map.entries()).map(([name, groupItems]) => ({ name, items: groupItems }));
  }, [filtered]);

  // Client-side paging over file groups: the evidence call already returns
  // every rule, but rendering 1,000 group rows at once is what makes the panel
  // crawl. Filters/search/tab changes go back to page 1.
  const pageCount = Math.max(1, Math.ceil(groups.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedGroups = useMemo(
    () => groups.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [groups, currentPage, pageSize]
  );
  const pagedItems = useMemo(() => pagedGroups.flatMap((group) => group.items), [pagedGroups]);

  const normalizedView = server.active ? server.normalized : normalized;
  const tabsView = server.active
    ? server.tabs.map((tab) => ({
        name: tab.name,
        items: [],
        total: tab.total,
        counts: tab.counts,
      }))
    : tabs;
  const currentTabView =
    tabsView.find((tab) => tab.name === (server.active ? server.activeTab : activeTab)) ||
    tabsView[0] ||
    null;
  const tabItemsCount = server.active
    ? server.pageData?.tab_total ?? normalizedView.length
    : currentTabView
      ? currentTabView.items.length
      : 0;
  const countsView = server.active ? server.counts : counts;
  const groupsView = server.active ? server.groups : groups;
  const pagedGroupsView = server.active ? server.pagedGroups : pagedGroups;
  const pagedItemsView = server.active ? server.pagedItems : pagedItems;
  const pageCountView = server.active ? server.pageCount : pageCount;
  const currentPageView = server.active ? server.currentPage : currentPage;
  const filesTotalView = server.active ? server.filesTotal : groups.length;
  const setPageView = server.active ? server.setPage : setPage;
  const setPageSizeView = server.active ? server.setPageSize : setPageSize;
  const pageSizeView = server.active ? server.pageSize : pageSize;
  const activeTabView = server.active ? server.activeTab : activeTab;
  const setActiveTabView = server.active ? server.setActiveTab : setActiveTab;
  const setStatusFilterView = server.active ? server.setStatusFilter : setStatusFilter;
  const statusFilterView = server.active ? server.statusFilter : statusFilter;
  const setQueryView = server.active ? server.setQuery : setQuery;
  const queryView = server.active ? server.query : query;

  useEffect(() => {
    if (!server.active) setPage(1);
  }, [activeTab, statusFilter, query, pageSize, server.active]);

  useEffect(() => {
    const names = pagedGroupsView.map((group) => group.name);
    const key = names.join('|');
    if (names.length > 1 && autoCollapsedKey.current !== key) {
      autoCollapsedKey.current = key;
      setCollapsedGroups(new Set(names.slice(1)));
    }
  }, [pagedGroupsView]);

  const toggleGroup = (name) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const toggleRule = (id) => {
    setExpandedRules((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (server.active) {
      server.ensureDetails([id]).catch(() => {});
    }
  };
  // "Expand all" must open the file groups as well as the rule details: every
  // group after the first is auto-collapsed, so toggling only the rule rows
  // inside collapsed groups changed nothing on screen for a 135-file batch.
  const allVisibleExpanded =
    pagedItemsView.length > 0 &&
    pagedGroupsView.every((group) => !collapsedGroups.has(group.name)) &&
    pagedItemsView.every((item) => expandedRules.has(item.id));
  const toggleAllRules = () => {
    const groupNames = pagedGroupsView.map((group) => group.name);
    if (allVisibleExpanded) {
      setExpandedRules((previous) => {
        const next = new Set(previous);
        pagedItemsView.forEach((item) => next.delete(item.id));
        return next;
      });
      // A single group stays open (collapsing it would hide the whole list).
      if (groupNames.length > 1) {
        setCollapsedGroups((previous) => new Set([...previous, ...groupNames]));
      }
      return;
    }
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      groupNames.forEach((name) => next.delete(name));
      return next;
    });
    setExpandedRules((previous) => {
      const next = new Set(previous);
      pagedItemsView.forEach((item) => next.add(item.id));
      return next;
    });
    if (server.active) {
      server.ensureDetails(pagedItemsView.map((item) => item.id)).catch(() => {});
    }
  };

  const statusTabs = [
    { value: '', label: 'All', count: tabItemsCount },
    ...VALIDATION_STATUS_ORDER.map((status) => ({
      value: status,
      label: status,
      count: countsView[status],
    })),
  ];

  const hasRules = server.active ? server.totalRuleCount > 0 : normalizedView.length > 0;

  return (
    <section className="rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
      {server.loadError ? (
        <div className="border-b border-eorig-border px-4 py-2 text-sm text-rose-700">{server.loadError}</div>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-eorig-border px-4 pt-3">
        {tabsView.length ? (
          <div role="tablist" aria-label="Validation tabs" className="flex flex-wrap gap-5">
            {tabsView.map((tab) => {
              const active = currentTabView && tab.name === currentTabView.name;
              return (
                <button
                  key={tab.name}
                  type="button"
                  role="tab"
                  aria-selected={Boolean(active)}
                  onClick={() => {
                    setActiveTabView(tab.name);
                    setStatusFilter('');
                  }}
                  className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 text-sm font-semibold transition-colors ${
                    active
                      ? 'border-eorig-navy-600 text-eorig-ink'
                      : 'border-transparent text-eorig-muted hover:text-eorig-ink'
                  }`}
                >
                  {tab.name}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                      active ? 'bg-eorig-navy-600 text-white' : 'bg-eorig-surface text-eorig-muted'
                    }`}
                  >
                    {tab.total ?? tab.items.length}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="pb-2.5">
            <h3 className="text-sm font-semibold text-eorig-ink">Validation</h3>
            <p className="mt-0.5 text-[11px] text-eorig-muted">
              ABDP Process rules for this loan — same Yes / No / NA / Error results as Process.
            </p>
          </div>
        )}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {loanTape === null ? (
            <span className="text-[11px] text-eorig-muted">No loan tape attached</span>
          ) : loanTape ? (
            <span
              className="max-w-[16rem] truncate text-[11px] text-eorig-muted"
              title={loanTape.file_name}
            >
              Loan tape: {loanTape.file_name} · {loanTape.row_count} row(s)
            </span>
          ) : null}
          {canMutateVa && loanTape && onRunTapeValidate ? (
            <button
              type="button"
              disabled={vaBusy}
              onClick={() => onRunTapeValidate()}
              className="rounded-full border border-eorig-navy-600 bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:bg-eorig-surface disabled:opacity-50"
            >
              Compare with loan tape
            </button>
          ) : null}
          {canMutateVa ? (
            <button
              type="button"
              disabled={vaBusy || !onRunValidate}
              onClick={() => onRunValidate?.()}
              className="rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50"
            >
              {vaBusy ? 'Running…' : hasRules ? 'Update validation' : 'Run validation'}
            </button>
          ) : null}
        </div>
      </div>

      {normalizedView.length || server.loading ? (
        <>
          {server.loading && !normalizedView.length ? (
            <p className="border-b border-eorig-border px-4 py-8 text-sm text-eorig-muted">
              Loading validation rules…
            </p>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-eorig-border bg-eorig-surface/40 px-4 py-2.5">
            <FilterTabs
              tabs={statusTabs}
              value={statusFilterView}
              onChange={setStatusFilterView}
              ariaLabel="Validation status"
            />
            <div className="flex flex-wrap items-center gap-3">
              {filesTotalView > 1 ? (
                <span className="text-[11px] text-eorig-muted">
                  {filesTotalView > pageSizeView
                    ? `${(currentPageView - 1) * pageSizeView + 1}–${Math.min(currentPageView * pageSizeView, filesTotalView)} of ${filesTotalView} files`
                    : `${filesTotalView} files`}
                </span>
              ) : null}
              <button
                type="button"
                onClick={toggleAllRules}
                disabled={!pagedItemsView.length}
                className="text-[11px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:opacity-50"
              >
                {allVisibleExpanded ? 'Collapse all' : 'Expand all'}
              </button>
              <input
                type="search"
                value={queryView}
                onChange={(event) => setQueryView(event.target.value)}
                placeholder="Search rules or files"
                aria-label="Search validation rules"
                className="w-full rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs text-eorig-ink placeholder:text-eorig-muted focus:border-eorig-navy-600 focus:outline-none sm:w-56"
              />
            </div>
          </div>

          <div className="grid grid-cols-[1fr_120px_90px] gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-eorig-muted sm:grid-cols-[1fr_140px_110px_90px]">
            <span className="pl-6">Rules</span>
            <span>Validation</span>
            <span className="hidden sm:block">Result</span>
            <span className="text-right">Evidence</span>
          </div>

          {groupsView.length ? (
            <ul className="border-t border-eorig-border">
              {pagedGroupsView.map((group) => {
                const groupCounts = countValidationStatuses(group.items);
                const isCollapsed = collapsedGroups.has(group.name);
                return (
                  <li key={group.name} className="border-b border-eorig-border last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.name)}
                      aria-expanded={!isCollapsed}
                      className="flex w-full items-center justify-between gap-3 bg-eorig-surface/30 px-4 py-2.5 text-left hover:bg-eorig-surface/60"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Chevron open={!isCollapsed} />
                        <span className="truncate text-sm font-semibold text-eorig-ink">
                          {group.name}
                        </span>
                        <span className="text-[11px] text-eorig-muted">
                          {group.items.length} {group.items.length === 1 ? 'rule' : 'rules'}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        {VALIDATION_STATUS_ORDER.filter((status) => groupCounts[status] > 0).map(
                          (status) => (
                            <span
                              key={status}
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${validationStatusClass(status)}`}
                            >
                              {groupCounts[status]} {status}
                            </span>
                          )
                        )}
                      </span>
                    </button>
                    {!isCollapsed ? (
                      <ul>
                        {group.items.map((item) => (
                          <ValidationRuleRow
                            key={item.id}
                            item={item}
                            expanded={expandedRules.has(item.id)}
                            onToggle={() => toggleRule(item.id)}
                          />
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="border-t border-eorig-border px-4 py-8 text-sm text-eorig-muted">
              No rules match this filter.
            </p>
          )}
          {pageCountView > 1 || filesTotalView > VALIDATION_PAGE_SIZES[0] ? (
            <Pagination
              aria-label="Validation files pagination"
              page={currentPageView}
              pageSize={pageSizeView}
              total={filesTotalView}
              pageSizes={VALIDATION_PAGE_SIZES}
              onPageChange={setPageView}
              onPageSizeChange={(size) => {
                setPageSizeView(size);
                setPageView(1);
              }}
              showItemRange={filesTotalView > pageSizeView}
              className="border-t border-eorig-border px-4 py-2.5"
            />
          ) : null}
        </>
      ) : (
        <p className="px-4 py-8 text-sm text-eorig-muted">
          No Process validation rules yet. Mark every processed folder reviewed in ABDP so
          Process builds Validation Credit / Validation Documentation for all PDFs, then Run
          validation.
        </p>
      )}
    </section>
  );
}


function buildClassificationJobs(facts) {
  const byJob = new Map();
  let summary = '';
  for (const fact of facts || []) {
    const key = fact.key || '';
    if (key === 'ABDP_SYNC_DETAIL') {
      continue;
    }
    if (key === 'DOCUMENT_TYPE') {
      const raw = fact.value || '';
      if (raw === 'CLASSIFY_PENDING_OR_EMPTY') {
        // Keep operator messaging clean — do not dump ABDP_SYNC_DETAIL keys here.
        summary =
          'Document types are not ready yet — Process is still classifying this batch. eVault re-checks Process every minute in the background and this page refreshes when types arrive; no need to click Run classification again.';
      } else {
        summary = raw;
      }
      continue;
    }
    const typeMatch = key.match(/^DOCUMENT_TYPE_(.+)$/);
    if (typeMatch) {
      const jobId = typeMatch[1];
      const row = byJob.get(jobId) || { id: jobId, label: jobId };
      row.docType = fact.value;
      row.label = fact.value || jobId;
      byJob.set(jobId, row);
      continue;
    }
    const pageMatch = key.match(/^PAGE_COUNT_(.+)$/);
    if (pageMatch) {
      const jobId = pageMatch[1];
      const row = byJob.get(jobId) || { id: jobId, label: jobId };
      row.pageCount = fact.value;
      byJob.set(jobId, row);
      continue;
    }
    const startMatch = key.match(/^START_PAGE_(.+)$/);
    if (startMatch) {
      const jobId = startMatch[1];
      const row = byJob.get(jobId) || { id: jobId, label: jobId };
      row.startPage = Number(fact.value) || undefined;
      byJob.set(jobId, row);
      continue;
    }
    const pagesMatch = key.match(/^PAGES_(.+)$/);
    if (pagesMatch) {
      const jobId = pagesMatch[1];
      const row = byJob.get(jobId) || { id: jobId, label: jobId };
      row.pages = fact.value;
      byJob.set(jobId, row);
      continue;
    }
    // Multi-PDF ZIP: ABDP creates one JD per PDF; SOURCE_FILE_{JD} names it.
    const sourceMatch = key !== 'SOURCE_FILE_NAME' && key.match(/^SOURCE_FILE_(.+)$/);
    if (sourceMatch) {
      const jobId = sourceMatch[1];
      const row = byJob.get(jobId) || { id: jobId, label: jobId };
      row.fileName = fact.value;
      byJob.set(jobId, row);
    }
  }
  const jobs = Array.from(byJob.values())
    .filter((job) => job.docType && job.docType !== 'CLASSIFY_PENDING_OR_EMPTY')
    .sort(compareDocumentJobs);
  return { jobs, summary };
}

function isOtherDocumentType(label) {
  const normalized = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
  return (
    normalized === 'other' ||
    normalized === 'others' ||
    normalized.startsWith('other ') ||
    normalized.endsWith(' other') ||
    normalized.includes(' others')
  );
}

/** Alphabetical by label; “Other(s)” types always last. */
function compareDocumentJobs(a, b) {
  const aOther = isOtherDocumentType(a.label || a.docType);
  const bOther = isOtherDocumentType(b.label || b.docType);
  if (aOther !== bOther) return aOther ? 1 : -1;
  const labelA = String(a.label || a.docType || a.id || '').toLowerCase();
  const labelB = String(b.label || b.docType || b.id || '').toLowerCase();
  if (labelA < labelB) return -1;
  if (labelA > labelB) return 1;
  return String(a.id || '').localeCompare(String(b.id || ''));
}

const AUTO_CHAIN_METHOD = '2';
const LEGACY_ABDP_UI = /finance(demo)?\.in-d\.ai/i;
const DEFAULT_ABDP_UI = String(
  process.env.REACT_APP_ABDP_UI_BASE_URL || 'https://ldp.intainabs.com'
).replace(/\/$/, '');

function resolveAbdpReviewUrl(facts) {
  const bId = (facts || []).find((f) => f.key === 'ABDP_B_ID')?.value;
  const fromFact = (facts || []).find((f) => f.key === 'ABDP_REVIEW_URL')?.value;
  const raw = fromFact && String(fromFact).startsWith('http') ? String(fromFact) : '';
  if (bId) {
    if (raw && !LEGACY_ABDP_UI.test(raw)) return raw;
    return `${DEFAULT_ABDP_UI}?b_id=${encodeURIComponent(String(bId))}`;
  }
  if (raw && !LEGACY_ABDP_UI.test(raw)) return raw;
  return null;
}

/** ABDP queue method; '2' = upload auto-runs classify → extract → mark-as-reviewed. */
function readAbdpMethod(facts) {
  const value = (facts || []).find((f) => f.key === 'ABDP_QUEUE_METHOD')?.value;
  return value == null ? '' : String(value).trim();
}

// Method 2 (auto-chain) queue families when ABDP has not told us the method
// yet: PayZen (payzen / payzen_two) and Dynamic (dynamic / dynamic_lease).
function isPayZenHint(hint, facts) {
  if (readAbdpMethod(facts)) return false;
  const queue = (facts || []).find((f) => f.key === 'ABDP_QUEUE')?.value;
  return /payzen|dynamic/i.test(`${hint || ''} ${queue || ''}`);
}

function isZipName(name) {
  return typeof name === 'string' && /\.zip$/i.test(name.trim());
}

/** Source is a multi-PDF ZIP: ABDP_SOURCE_KIND fact, SOURCE_FILE_NAME, or the package file. */
function readSourceIsZip(facts, sourceDoc) {
  const kind = (facts || []).find((f) => f.key === 'ABDP_SOURCE_KIND')?.value;
  if (kind && String(kind).toLowerCase() === 'zip') return true;
  const fileNameFact = (facts || []).find((f) => f.key === 'SOURCE_FILE_NAME')?.value;
  if (isZipName(fileNameFact)) return true;
  return isZipName(sourceDoc?.fileName) || isZipName(sourceDoc?.label);
}

function splitExtractedFacts(facts) {
  return (facts || []).filter(
    (fact) => !isMetaFactKey(fact.key) && !isClassificationFactKey(fact.key)
  );
}

/** Fields for one JD — keys look like `Lessee name (JD_1_1)`. */
function fieldsForJob(facts, jobId) {
  if (!jobId) return [];
  const suffix = `(${jobId})`;
  const jdTagged = /\([^)]*JD_[^)]+\)/;
  return (facts || []).filter((fact) => {
    const key = String(fact.key || '');
    if (key.endsWith(suffix) || key.includes(` ${suffix}`)) return true;
    // Untagged legacy keys (no JD suffix) show under every selected type.
    return !jdTagged.test(key);
  });
}

function displayFieldLabel(key, jobId) {
  const raw = String(key || '');
  if (!jobId) return raw;
  const suffix = ` (${jobId})`;
  return raw.endsWith(suffix) ? raw.slice(0, -suffix.length) : raw;
}

function FactList({
  facts,
  emptyMessage,
  jobId,
  canVerify = false,
  onVerifyFact,
  verifyBusy = false,
  canEdit = false,
  draftValues = {},
  onDraftChange,
}) {
  if (!facts?.length) {
    return <p className="px-4 py-8 text-sm text-eorig-muted">{emptyMessage}</p>;
  }
  return (
    <ul className="divide-y divide-eorig-border">
      {facts.map((fact) => {
        const verified = fact.verificationStatus === 'verified';
        const factId = fact.id || fact.key;
        const draft =
          fact.id && Object.prototype.hasOwnProperty.call(draftValues, fact.id)
            ? draftValues[fact.id]
            : fact.value ?? '';
        const dirty =
          canEdit &&
          fact.id &&
          Object.prototype.hasOwnProperty.call(draftValues, fact.id) &&
          String(draftValues[fact.id] ?? '') !== String(fact.value ?? '');
        return (
          <li
            key={factId}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-2 text-sm"
          >
            <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
              {displayFieldLabel(fact.key, jobId)}
              {dirty ? (
                <span className="ml-1 font-semibold normal-case text-amber-700">· edited</span>
              ) : null}
            </p>
            <div className="min-w-0">
              {canEdit && fact.id ? (
                <input
                  type="text"
                  value={draft}
                  onChange={(event) => onDraftChange?.(fact.id, event.target.value)}
                  className="w-full rounded-lg border border-eorig-border bg-white px-2 py-1 text-sm font-medium text-eorig-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
                  aria-label={displayFieldLabel(fact.key, jobId)}
                />
              ) : (
                <p className="break-words font-medium text-eorig-ink">{fact.value ?? '—'}</p>
              )}
              {verified && fact.verifiedByLabel ? (
                <p className="mt-0.5 text-[10px] text-eorig-muted">
                  Verified by {fact.verifiedByLabel}
                  {fact.verifiedAt
                    ? ` · ${new Date(fact.verifiedAt).toLocaleString()}`
                    : ''}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {verified ? (
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-xs text-emerald-700"
                  aria-label="Verified"
                  title="Verified"
                >
                  ✓
                </span>
              ) : (
                <span className="text-[10px] font-semibold uppercase text-amber-700">
                  Unverified
                </span>
              )}
              {canVerify && !verified && fact.id ? (
                <button
                  type="button"
                  disabled={verifyBusy || !onVerifyFact || dirty}
                  onClick={() => onVerifyFact?.(fact.id)}
                  className="rounded-full border border-eorig-border px-2 py-0.5 text-[10px] font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40 disabled:opacity-50"
                >
                  Verify
                </button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ExtractionDisclaimer({ extractionState, fieldTotal, fieldVerified }) {
  if (!extractionState || extractionState === 'none' || extractionState === 'verified') {
    return null;
  }
  const mixed = extractionState === 'mixed';
  return (
    <div
      className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900"
      role="status"
    >
      {mixed
        ? `${fieldVerified} of ${fieldTotal} extracted fields are verified. Unverified values were produced by automated document processing and may be incorrect.`
        : 'These extracted fields were produced by automated document processing and may be incorrect. They have not been verified.'}
    </div>
  );
}

function guessMimeFromName(fileName) {
  if (!fileName || typeof fileName !== 'string') return '';
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.txt') || lower.endsWith('.csv') || lower.endsWith('.md')) {
    return 'text/plain';
  }
  if (lower.endsWith('.json')) return 'application/json';
  return '';
}

function SourceDocumentPane({
  activeDoc,
  loadDocumentPreview,
  listDocumentMembers,
  startPage,
  pageCount,
  pages,
  emptyMessage = 'Select a document to preview the uploaded source file.',
  labelledCopies = false,
  onDownloadCopy,
  sourceIsZip = false,
  jobFileName = '',
  jobCount = 0,
  abdpReviewUrl = null,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [textContent, setTextContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scopedPageTotal, setScopedPageTotal] = useState(null);
  const [fullPdfUrl, setFullPdfUrl] = useState(null);
  const [scopedMeta, setScopedMeta] = useState(null);
  const [zipMembers, setZipMembers] = useState([]);
  const [zipShownName, setZipShownName] = useState('');
  const [zipPick, setZipPick] = useState('');
  const zipSource =
    sourceIsZip || isZipName(activeDoc?.fileName) || isZipName(activeDoc?.label);
  const zipMember = zipPick || jobFileName || '';

  useEffect(() => {
    setZipPick('');
  }, [jobFileName, activeDoc?.id]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let fullUrl = null;

    async function loadPreview() {
      setPreviewUrl(null);
      setMimeType('');
      setTextContent('');
      setError('');
      setScopedPageTotal(null);
      setFullPdfUrl(null);
      setScopedMeta(null);
      setZipMembers([]);
      setZipShownName('');

      if (!activeDoc?.id) {
        setLoading(false);
        return;
      }
      if (typeof loadDocumentPreview !== 'function') {
        setError('Source preview is not available for this view.');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let result = null;
        let serverMember = null;
        if (zipSource && typeof listDocumentMembers === 'function') {
          // Large batch ZIPs: ask the API for the member list and for ONE
          // labelled member. Downloading the whole archive into the browser
          // fails with "Failed to fetch" for 1,000-PDF batches.
          let members = null;
          try {
            const listing = await listDocumentMembers(activeDoc);
            members = Array.isArray(listing?.items) ? listing.items : null;
          } catch (listError) {
            // Members endpoint unavailable (older backend / transient): fall
            // back to the whole-archive download below.
            console.warn('ZIP member listing failed', listError);
            members = null;
          }
          if (cancelled) return;
          if (members) {
            const { pickZipMember } = await import('./zipPdf');
            if (!members.length) {
              throw new Error('This ZIP does not contain any PDF files.');
            }
            const chosen = pickZipMember(members, zipMember);
            if (!chosen) {
              throw new Error(
                zipMember
                  ? `No PDF named ${zipMember} in this ZIP (${members.length} PDF member(s)).`
                  : 'Select a classified document to open a PDF from this ZIP.'
              );
            }
            result = await loadDocumentPreview(activeDoc, { member: chosen });
            serverMember = { members, chosen };
          }
        }
        if (!result) {
          result = await loadDocumentPreview(activeDoc);
        }
        if (cancelled) return;
        const blob = result?.blob;
        if (!blob) {
          setPreviewUrl(null);
          setMimeType('');
          setTextContent('');
          setError('Uploaded source file could not be loaded.');
          return;
        }
        const resolvedMime =
          result.mimeType ||
          blob.type ||
          guessMimeFromName(activeDoc.fileName || activeDoc.label) ||
          'application/octet-stream';

        let displayBlob = blob;
        let displayMime = resolvedMime;
        let scopedTotal = null;
        let fromZip = false;
        const archiveName = activeDoc.fileName || activeDoc.label || '';
        if (serverMember && !isZipSource({ mimeType: resolvedMime, blob })) {
          displayMime = 'application/pdf';
          fromZip = true;
          const { zipMemberBasename } = await import('./zipPdf');
          if (!cancelled) {
            setZipMembers(serverMember.members.map(zipMemberBasename));
            setZipShownName(zipMemberBasename(serverMember.chosen));
          }
        } else if (zipSource || isZipSource({ mimeType: resolvedMime, fileName: archiveName, blob })) {
          const { extractPdfFromZip } = await import('./zipPdf');
          const extracted = await extractPdfFromZip(blob, zipMember);
          displayBlob = extracted.blob;
          displayMime = 'application/pdf';
          fromZip = true;
          if (!cancelled) {
            setZipMembers(extracted.members);
            setZipShownName(extracted.fileName);
          }
        }
        const scope = {
          startPage: startPage ?? activeDoc.startPage,
          pageCount: pageCount ?? activeDoc.pageCount,
          pages: pages ?? activeDoc.pages,
        };

        // ZIP members are already one PDF per JD — ABDP page numbers refer to
        // Process images, not this file, so do not slice.
        if (displayMime.includes('pdf') && !fromZip) {
          const { buildScopedPdfBlob, scopeWasRequested } = await import('./scopedPdf');

          if (scopeWasRequested(scope)) {
            fullUrl = URL.createObjectURL(displayBlob);
            try {
              const sliced = await buildScopedPdfBlob(blob, scope);
              if (!sliced?.blob) {
                const start = Number(scope.startPage) || null;
                const count = Number(scope.pageCount) || null;
                setError(
                  start
                    ? `Page range from p.${start}${count ? ` (${count} page(s))` : ''} is outside this uploaded file.`
                    : 'Page range for this type is outside the uploaded file.'
                );
                setFullPdfUrl(fullUrl);
                fullUrl = null;
                setMimeType(displayMime);
                return;
              }
              displayBlob = sliced.blob;
              scopedTotal = sliced.pageCount;
              if (!cancelled) {
                setScopedMeta({
                  fitted: Boolean(sliced.fitted),
                  fileStart: sliced.scope?.startPage || null,
                  abdpStart: sliced.abdpStartPage || Number(scope.startPage) || null,
                });
              }
            } catch (sliceError) {
              console.warn('Scoped PDF slice failed', sliceError);
              setError(
                'Could not isolate pages for this document type. Open the full file if needed.'
              );
              setFullPdfUrl(fullUrl);
              fullUrl = null;
              setMimeType(displayMime);
              return;
            }
          }
        }

        objectUrl = URL.createObjectURL(displayBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          if (fullUrl) {
            URL.revokeObjectURL(fullUrl);
            fullUrl = null;
          }
          return;
        }
        setPreviewUrl(objectUrl);
        setMimeType(displayMime);
        setScopedPageTotal(scopedTotal);
        if (fullUrl) {
          URL.revokeObjectURL(fullUrl);
          fullUrl = null;
        }
        if (displayMime.startsWith('text/') || displayMime.includes('json')) {
          setTextContent(await blob.text());
        }
      } catch (requestError) {
        if (!cancelled) {
          setPreviewUrl(null);
          setMimeType('');
          setTextContent('');
          setError(requestError.message || 'Unable to load the uploaded source document.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPreview();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (fullUrl) URL.revokeObjectURL(fullUrl);
    };
  }, [
    activeDoc?.id,
    activeDoc?.fileName,
    activeDoc?.startPage,
    activeDoc?.pageCount,
    activeDoc?.pages,
    startPage,
    pageCount,
    pages,
    loadDocumentPreview,
    listDocumentMembers,
    zipSource,
    zipMember,
  ]);

  useEffect(
    () => () => {
      if (fullPdfUrl) URL.revokeObjectURL(fullPdfUrl);
    },
    [fullPdfUrl]
  );

  if (!activeDoc?.id) {
    return <p className="px-4 py-10 text-sm text-eorig-muted">{emptyMessage}</p>;
  }
  const zipBar =
    zipSource || zipMembers.length ? (
      <div className="flex flex-wrap items-center gap-2 border-b border-eorig-border px-3 py-1.5 text-[11px] text-eorig-muted">
        <span>
          Showing {zipShownName || zipMember || 'PDF'} from{' '}
          {activeDoc.fileName || activeDoc.label || 'ZIP'}
          {jobCount ? ` · ${jobCount} document${jobCount === 1 ? '' : 's'}` : ''}
        </span>
        {zipMembers.length > 1 ? (
          <label className="inline-flex items-center gap-1 font-semibold text-eorig-ink">
            File
            <select
              className="rounded border border-eorig-border bg-white px-1 py-0.5 text-[11px] font-normal"
              value={zipShownName || zipMember}
              onChange={(event) => setZipPick(event.target.value)}
              aria-label="PDF in ZIP"
            >
              {zipMembers.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {abdpReviewUrl ? (
          <a
            href={abdpReviewUrl}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            title="Opens ABDP Process. Sign in with your ABDP account at ldp.intainabs.com — eVault login will not work there."
          >
            Open in ABDP Process →
          </a>
        ) : null}
      </div>
    ) : null;
  if (loading) {
    return (
      <div>
        {zipBar}
        <p className="px-4 py-10 text-sm text-eorig-muted">
          {zipSource ? 'Opening PDF from ZIP…' : 'Loading scoped pages…'}
        </p>
      </div>
    );
  }
  if (error && !previewUrl) {
    return (
      <div className="space-y-3 px-4 py-8 text-sm">
        {zipBar}
        <p className="text-amber-800">{error}</p>
        {fullPdfUrl ? (
          <a
            href={fullPdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            Open full PDF
          </a>
        ) : null}
      </div>
    );
  }

  const scopeStart = Number(startPage ?? activeDoc.startPage) || null;
  const scopeCount =
    scopedPageTotal || Number(pageCount ?? activeDoc.pageCount) || null;

  if (previewUrl && mimeType.includes('pdf')) {
    return (
      <div className="flex h-[70dvh] min-h-[32rem] max-h-[52rem] flex-col">
        {zipBar}
        {!zipSource && (scopeStart || scopeCount || scopedMeta) ? (
          <p className="border-b border-eorig-border px-3 py-1.5 text-[11px] text-eorig-muted">
            {scopedPageTotal
              ? `Showing ${scopedPageTotal} scoped page(s)`
              : scopeStart
                ? `Pages from ${scopeStart}`
                : 'Scoped pages'}
            {scopedMeta?.fitted && scopedMeta.fileStart
              ? ` · file p.${scopedMeta.fileStart}`
              : scopedPageTotal && scopeStart
                ? ` · from p.${scopeStart}`
                : null}
            {scopedMeta?.fitted && scopedMeta.abdpStart
              ? ` · ABDP p.${scopedMeta.abdpStart} was outside file`
              : null}
            {!scopedPageTotal && scopeCount ? ` · ${scopeCount} page(s)` : null}
          </p>
        ) : null}
        <iframe
          key={previewUrl}
          title={zipShownName || activeDoc.fileName || activeDoc.label || 'Source PDF'}
          src={`${previewUrl}#toolbar=1&navpanes=0`}
          className="h-full w-full flex-1 bg-eorig-surface"
        />
      </div>
    );
  }

  if (previewUrl && mimeType.startsWith('image/')) {
    return (
      <div className="flex max-h-[32rem] items-center justify-center overflow-auto p-4">
        <img
          src={previewUrl}
          alt={activeDoc.fileName || activeDoc.label || 'Source'}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }

  if (textContent) {
    return (
      <pre className="max-h-[32rem] overflow-auto px-4 py-3 text-xs text-eorig-ink">
        {textContent}
      </pre>
    );
  }

  return (
    <div className="space-y-2 px-4 py-8 text-center">
      <p className="text-sm text-eorig-muted">Preview not available for this file type.</p>
      {previewUrl && !labelledCopies ? (
        <a
          href={previewUrl}
          download={activeDoc.fileName || 'document'}
          className="text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          Download source file
        </a>
      ) : null}
      {labelledCopies && onDownloadCopy ? (
        <button
          type="button"
          className="text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          onClick={() => onDownloadCopy(activeDoc)}
        >
          Download non-authoritative copy
        </button>
      ) : null}
    </div>
  );
}

function JobCards({ jobs, selectedId, onSelect, emptyMessage, copyBadge }) {
  if (!jobs.length) {
    return <p className="px-4 py-8 text-sm text-eorig-muted">{emptyMessage}</p>;
  }
  const otherJobs = jobs.filter((job) => isOtherDocumentType(job.label || job.docType));
  const mainJobs = jobs.filter((job) => !isOtherDocumentType(job.label || job.docType));

  function renderCard(job, index) {
    const active = job.id === selectedId;
    return (
      <li key={job.id}>
        <button
          type="button"
          onClick={() => onSelect?.(job.id)}
          className={`w-full rounded-xl border px-3 py-3 text-left transition-colors ${
            active
              ? 'border-eorig-cyan bg-eorig-cyan/10'
              : 'border-eorig-border bg-white hover:border-eorig-navy-600/40'
          }`}
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-eorig-muted">
            {padThumbIndex(index)} · {job.id}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-eorig-ink">
            {job.label}
            {copyBadge ? (
              <span className="rounded-full border border-eorig-border bg-eorig-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-eorig-muted">
                {copyBadge}
              </span>
            ) : null}
          </p>
          <p className="mt-1 text-xs text-eorig-muted">
            {job.pageCount ? `${job.pageCount} page(s)` : 'Pages —'}
            {job.startPage ? ` · starts p.${job.startPage}` : ''}
          </p>
          {job.fileName ? (
            <p className="mt-1 truncate text-[11px] text-eorig-muted" title={job.fileName}>
              {job.fileName}
            </p>
          ) : null}
        </button>
      </li>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="digitize-job-cards">
      <p className="border-b border-eorig-border bg-white px-4 py-2 text-[11px] text-eorig-muted">
        {jobs.length} document type{jobs.length === 1 ? '' : 's'}
        {otherJobs.length
          ? ` · ${otherJobs.length} Other(s) at the bottom — scroll down`
          : ''}
      </p>
      <ul className="grid gap-2 p-3 sm:grid-cols-2">
        {mainJobs.map((job, index) => renderCard(job, index))}
      </ul>
      {otherJobs.length ? (
        <div className="border-t border-dashed border-eorig-border">
          <p className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
            Other / unclassified
          </p>
          <ul className="grid gap-2 p-3 sm:grid-cols-2">
            {otherJobs.map((job, index) => renderCard(job, mainJobs.length + index))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function DocumentTypeSelect({ jobs, selectedId, onSelect, id = 'digitize-doc-type' }) {
  if (!jobs.length) return null;
  return (
    <label
      htmlFor={id}
      className="flex min-w-0 max-w-md flex-1 items-center gap-2 text-[11px] font-semibold text-eorig-muted"
    >
      <span className="shrink-0">Document</span>
      <select
        id={id}
        className="min-w-0 flex-1 truncate rounded-lg border border-eorig-border bg-white px-2.5 py-1.5 text-sm font-medium text-eorig-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
        value={selectedId || ''}
        onChange={(event) => onSelect?.(event.target.value)}
      >
        {jobs.map((job, index) => (
          <option key={job.id} value={job.id}>
            {padThumbIndex(index)} · {job.fileName || job.label}
            {job.fileName && job.label ? ` · ${job.label}` : ''}
            {job.pageCount ? ` (${job.pageCount}p)` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function ClassificationTypeEditor({
  job,
  documentTypes = [],
  busy = false,
  onSaveType,
}) {
  const [selectedTypeId, setSelectedTypeId] = useState('');

  useEffect(() => {
    if (!job) {
      setSelectedTypeId('');
      return;
    }
    const match = (documentTypes || []).find(
      (opt) =>
        opt.label === job.label ||
        opt.id === job.label ||
        opt.id === job.docTypeId
    );
    setSelectedTypeId(match?.id || '');
  }, [job?.id, job?.label, job?.docTypeId, documentTypes]);

  if (!job) return null;
  const options = documentTypes?.length
    ? documentTypes
    : job.label
      ? [{ id: job.label, label: job.label }]
      : [];
  const selected = options.find((opt) => opt.id === selectedTypeId);
  const dirty =
    selected &&
    selected.label !== job.label &&
    selected.id !== job.label;

  return (
    <div className="flex flex-wrap items-end gap-2 border-t border-eorig-border px-4 py-3">
      <label className="min-w-[12rem] flex-1 text-[11px] font-semibold text-eorig-muted">
        Change type for {job.id}
        <select
          className="mt-1 w-full rounded-lg border border-eorig-border bg-white px-2.5 py-1.5 text-sm font-medium text-eorig-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
          value={selectedTypeId}
          onChange={(event) => setSelectedTypeId(event.target.value)}
          disabled={busy || !options.length}
        >
          <option value="">Select document type…</option>
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        disabled={busy || !dirty || !selectedTypeId || !onSaveType}
        onClick={() =>
          onSaveType?.({
            jobId: job.id,
            documentTypeId: selectedTypeId,
            documentTypeLabel: selected?.label || selectedTypeId,
          })
        }
        className="rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save type'}
      </button>
    </div>
  );
}

/**
 * Digitize workspace — ABDP-style flow: Classify → Extract → Review → Validation.
 * Only renders values passed in — no fabricated fields.
 */
// Stable defaults: `facts` feeds effect deps (draft reset), so a fresh `[]` per
// render would re-fire them forever when the prop is omitted.
const EMPTY_LIST = Object.freeze([]);

export function DigitizeWorkspace({
  title,
  subtitle,
  documents = EMPTY_LIST,
  facts = EMPTY_LIST,
  validationItems = EMPTY_LIST,
  validationAssetId,
  fetchValidationPage,
  fetchValidationDetails,
  validationReloadToken = 0,
  loading = false,
  error = '',
  onRetry,
  selectedDocumentId,
  onSelectDocument,
  loadDocumentPreview,
  listDocumentMembers,
  canMutateVa = false,
  canSyncFromAbdp,
  onRunClassify,
  onRunExtract,
  onRunValidate,
  // Loan tape summary from the backend: object when attached, null when the
  // package has none, undefined while unknown.
  loanTape,
  onRunTapeValidate,
  onSyncReview,
  onMarkReviewed,
  documentTypes = [],
  onSaveClassification,
  onSaveExtractedFields,
  editBusy = false,
  vaBusy = false,
  vaMessage = '',
  extractEnabled = false,
  extractionState = 'none',
  fieldTotal = 0,
  fieldVerified = 0,
  onVerifyFact,
  onVerifyDocumentType,
  verifyBusy = false,
  labelledCopies = false,
  copyContext = null,
  onDownloadCopy,
  onRequestCertifiedCopy,
  queueHint = '',
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const tab = STEP_VALUES.has(stepParam) ? stepParam : 'classify';

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('step', next);
    setSearchParams(params, { replace: true });
  };

  const { jobs, summary } = useMemo(() => buildClassificationJobs(facts), [facts]);
  const extracted = useMemo(() => splitExtractedFacts(facts), [facts]);
  const abdpReviewUrl = useMemo(() => resolveAbdpReviewUrl(facts), [facts]);
  const abdpBatchId = useMemo(
    () => (facts || []).find((f) => f.key === 'ABDP_B_ID')?.value || null,
    [facts]
  );

  const sourceDoc = documents[0] || null;
  // Method 2 queues (PayZen): ABDP auto-runs classify → extract → review on upload.
  // Digitize must offer Sync first and never re-trigger extraction there.
  const abdpMethod = readAbdpMethod(facts);
  const autoChain = abdpMethod === AUTO_CHAIN_METHOD || isPayZenHint(queueHint, facts);
  const sourceIsZip = readSourceIsZip(facts, sourceDoc);
  const folderInReview = useMemo(() => {
    const state = (facts || []).find((f) => f.key === 'ABDP_FOLDER_STATE')?.value;
    return ['review', 'exported'].includes(String(state || '').toLowerCase());
  }, [facts]);
  const autoExtracted = autoChain && (folderInReview || extracted.length > 0);
  const syncAllowed = Boolean((canSyncFromAbdp ?? canMutateVa) && onSyncReview);
  const runExtractDisabled = vaBusy || !onRunExtract || !extractEnabled || autoExtracted;
  const runExtractLabel = autoExtracted ? 'Already auto-extracted — Sync instead' : 'Run extraction';
  const runExtractTitle = autoExtracted
    ? 'ABDP (Method 2) already extracted this batch on upload. Use Sync verification from ABDP.'
    : autoChain
      ? 'Method 2 queue: ABDP extracts automatically after upload. Prefer Sync once Review is ready.'
      : extractEnabled
        ? 'Extract fields, then continue to Review'
        : 'Run classification first so ABDP types sync into Digitize';
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [draftValues, setDraftValues] = useState({});

  useEffect(() => {
    if (!jobs.length) {
      setSelectedJobId(null);
      return;
    }
    if (!selectedJobId || !jobs.some((job) => job.id === selectedJobId)) {
      setSelectedJobId(jobs[0].id);
    }
  }, [jobs, selectedJobId]);

  useEffect(() => {
    setDraftValues({});
  }, [facts]);

  const activeJobId = selectedJobId || jobs[0]?.id || null;
  const jobFields = useMemo(
    () => fieldsForJob(extracted, activeJobId),
    [extracted, activeJobId]
  );

  const dirtyFieldIds = useMemo(() => {
    const ids = [];
    for (const fact of jobFields) {
      if (!fact.id) continue;
      if (!Object.prototype.hasOwnProperty.call(draftValues, fact.id)) continue;
      if (String(draftValues[fact.id] ?? '') !== String(fact.value ?? '')) {
        ids.push(fact.id);
      }
    }
    return ids;
  }, [jobFields, draftValues]);

  const activeJob = jobs.find((job) => job.id === activeJobId) || jobs[0];
  const previewDoc = activeJob
    ? {
        id: sourceDoc?.id,
        label: activeJob.label,
        fileName: sourceDoc?.fileName,
        pageCount: activeJob.pageCount,
        startPage: activeJob.startPage,
        pages: activeJob.pages,
      }
    : sourceDoc;

  const handleDraftChange = (factId, value) => {
    setDraftValues((prev) => ({ ...prev, [factId]: value }));
  };

  const handleSaveFields = () => {
    if (!onSaveExtractedFields || !dirtyFieldIds.length) return;
    const updates = dirtyFieldIds.map((factId) => ({
      fact_id: factId,
      fact_value: draftValues[factId],
    }));
    onSaveExtractedFields(updates);
  };

  useEffect(() => {
    if (!selectedDocumentId && documents[0]?.id) {
      onSelectDocument?.(documents[0].id);
    }
  }, [documents, selectedDocumentId, onSelectDocument]);

  if (loading) {
    return (
      <section className="rounded-2xl border border-eorig-border bg-white p-5 text-sm text-eorig-muted shadow-eorig-card">
        Loading document review…
      </section>
    );
  }

  const fieldEditEnabled = Boolean(canMutateVa && onSaveExtractedFields);
  const saveFieldsButton =
    fieldEditEnabled && dirtyFieldIds.length ? (
      <button
        type="button"
        disabled={editBusy}
        onClick={handleSaveFields}
        className="rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50"
      >
        {editBusy ? 'Saving…' : `Save changes (${dirtyFieldIds.length})`}
      </button>
    ) : null;

  return (
    <div className="space-y-4">
      {error ? <ErrorBanner message={error} onRetry={onRetry} /> : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {title ? (
            <h2 className="text-base font-semibold text-eorig-ink">{title}</h2>
          ) : null}
          {subtitle ? (
            <p className="mt-1 text-xs text-eorig-muted">{subtitle}</p>
          ) : null}
        </div>
        <FilterTabs
          tabs={DIGITIZE_TABS}
          value={tab}
          onChange={setTab}
          ariaLabel="Digitize workflow steps"
        />
      </div>

      {vaMessage ? (
        <p className="text-xs text-eorig-muted">{vaMessage}</p>
      ) : null}

      {labelledCopies ? <CustodyCopyHeader context={copyContext} /> : null}

      {labelledCopies ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40 disabled:opacity-50"
            onClick={() => sourceDoc && onDownloadCopy?.(sourceDoc)}
            disabled={!sourceDoc || !onDownloadCopy}
          >
            {copyContext?.copy_kind === 'REVIEW'
              ? 'Download review copy'
              : copyContext?.copy_kind === 'SUPPORTING'
                ? 'Download supporting copy'
                : 'Download non-authoritative copy'}
          </button>
          {copyContext?.can_request_certified_copy && onRequestCertifiedCopy ? (
            <button
              type="button"
              className="rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40"
              onClick={() => onRequestCertifiedCopy(sourceDoc)}
            >
              Request certified copy
            </button>
          ) : null}
        </div>
      ) : null}

      {abdpBatchId || abdpReviewUrl ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-eorig-border bg-eorig-surface/40 px-3 py-2 text-xs text-eorig-muted">
          {abdpBatchId ? (
            <span>
              ABDP batch <span className="font-semibold text-eorig-ink">{abdpBatchId}</span>
            </span>
          ) : null}
          {abdpReviewUrl ? (
            <a
              href={abdpReviewUrl}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
              title="Opens ABDP Process. Sign in with your ABDP account at ldp.intainabs.com — eVault login will not work there."
            >
              Open in ABDP Process →
            </a>
          ) : null}
          {syncAllowed ? (
            <button
              type="button"
              disabled={vaBusy}
              onClick={() => onSyncReview?.()}
              className="rounded-full border border-eorig-border bg-white px-3 py-1 text-xs font-semibold text-eorig-ink hover:border-eorig-navy-600 disabled:opacity-50"
              title="After Mark as reviewed in Process UI, pull fields and mark Digitize verified"
            >
              {vaBusy ? 'Syncing…' : 'Sync verification from ABDP'}
            </button>
          ) : null}
          {canMutateVa && onMarkReviewed ? (
            <button
              type="button"
              disabled={editBusy || vaBusy}
              onClick={() => onMarkReviewed?.()}
              className="rounded-full border border-eorig-border bg-white px-3 py-1 text-xs font-semibold text-eorig-ink hover:border-eorig-navy-600 disabled:opacity-50"
              title="Mark folder reviewed in ABDP Process"
            >
              {editBusy ? 'Marking…' : 'Mark reviewed in ABDP'}
            </button>
          ) : null}
        </div>
      ) : null}

      {tab === 'classify' ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
          <section className="flex max-h-[calc(100dvh-11rem)] min-h-0 flex-col overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-eorig-border px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-eorig-ink">Classification</h3>
                <p className="mt-0.5 text-[11px] text-eorig-muted">
                  {autoChain
                    ? `This queue runs classify, extract and review automatically in ABDP (Method 2). ${
                        sourceIsZip ? 'Each PDF in the zip becomes one card. ' : ''
                      }Wait until the batch is in Review, then use Sync verification from ABDP — do not re-run extraction.`
                    : 'Page-wise document types from ABDP (one card per JD). Run extraction here when types are ready, then open Review for fields.'}
                </p>
              </div>
              {canMutateVa || syncAllowed ? (
                <div className="flex flex-wrap items-center gap-2">
                  {autoChain && syncAllowed ? (
                    <button
                      type="button"
                      disabled={vaBusy}
                      onClick={() => onSyncReview?.()}
                      title="Pull ABDP's auto-extracted fields and Process review state into Digitize"
                      className="rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50"
                    >
                      {vaBusy ? 'Syncing…' : 'Sync from ABDP'}
                    </button>
                  ) : null}
                  {canMutateVa ? (
                    <>
                      <button
                        type="button"
                        disabled={vaBusy || !onRunClassify}
                        onClick={() => onRunClassify?.()}
                        title={
                          autoChain
                            ? 'Upload once; re-run only if ABDP Process shows the batch as Failed'
                            : undefined
                        }
                        className={
                          autoChain
                            ? 'rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40 disabled:opacity-50'
                            : 'rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50'
                        }
                      >
                        {vaBusy ? 'Running…' : 'Run classification'}
                      </button>
                      <button
                        type="button"
                        disabled={runExtractDisabled}
                        onClick={() => onRunExtract?.()}
                        title={runExtractTitle}
                        className="rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40 disabled:opacity-50"
                      >
                        {vaBusy ? 'Running…' : runExtractLabel}
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            {autoChain && !folderInReview && jobs.length ? (
              <p className="border-b border-eorig-border bg-amber-50 px-4 py-2 text-xs text-amber-800">
                ABDP is still processing this batch{sourceIsZip ? ` (${jobs.length} document${jobs.length === 1 ? '' : 's'} from the zip)` : ''}.
                Fields appear once Process moves it to Review — use Sync from ABDP then.
              </p>
            ) : null}
            {summary && !jobs.length ? (
              <p className="px-4 py-3 text-sm text-amber-800">{summary}</p>
            ) : null}
            <JobCards
              jobs={jobs}
              selectedId={activeJobId}
              onSelect={setSelectedJobId}
              emptyMessage="No classification yet. Run classification to sync ABDP types here."
              copyBadge={labelledCopies ? copyBadgeLabel(sourceDoc?.copyKind || copyContext?.copy_kind) : null}
            />
            {canMutateVa && onSaveClassification && activeJob ? (
              <ClassificationTypeEditor
                job={activeJob}
                documentTypes={documentTypes}
                busy={editBusy}
                onSaveType={onSaveClassification}
              />
            ) : null}
          </section>
          <section className="rounded-2xl border border-eorig-border bg-white shadow-eorig-card lg:sticky lg:top-4">
            <div className="border-b border-eorig-border px-4 py-3">
              <h3 className="text-sm font-semibold text-eorig-ink">Source (page scope)</h3>
              <p className="mt-0.5 truncate text-[11px] text-eorig-muted">
                {activeJob
                  ? `${activeJob.label}${activeJob.fileName ? ` · ${activeJob.fileName}` : ''}${
                      activeJob.startPage ? ` · page ${activeJob.startPage}` : ''
                    }`
                  : sourceDoc?.fileName || 'Select a classified document'}
              </p>
            </div>
            <SourceDocumentPane
              activeDoc={previewDoc?.id ? previewDoc : sourceDoc}
              loadDocumentPreview={loadDocumentPreview}
              listDocumentMembers={listDocumentMembers}
              startPage={activeJob?.startPage}
              pageCount={activeJob?.pageCount}
              pages={activeJob?.pages}
              labelledCopies={labelledCopies}
              onDownloadCopy={onDownloadCopy}
              sourceIsZip={sourceIsZip}
              jobFileName={activeJob?.fileName || ''}
              jobCount={jobs.length}
              abdpReviewUrl={abdpReviewUrl}
            />
          </section>
        </div>
      ) : null}

      {tab === 'extract' ? (
        <div className="overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-eorig-border px-4 py-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              <h3 className="shrink-0 text-sm font-semibold text-eorig-ink">Extracted fields</h3>
              <DocumentTypeSelect
                id="extract-doc-type"
                jobs={jobs}
                selectedId={activeJobId}
                onSelect={setSelectedJobId}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {saveFieldsButton}
              {canMutateVa && onVerifyDocumentType && jobFields.some((f) => f.verificationStatus !== 'verified') ? (
                <button
                  type="button"
                  disabled={verifyBusy || !jobFields.length || dirtyFieldIds.length > 0}
                  onClick={() => onVerifyDocumentType?.(activeJobId)}
                  className="rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50"
                >
                  {verifyBusy ? 'Verifying…' : 'Verify all for type'}
                </button>
              ) : null}
              {syncAllowed ? (
                <button
                  type="button"
                  disabled={vaBusy}
                  onClick={() => onSyncReview?.()}
                  title="Pull ABDP's auto-extracted fields into Digitize"
                  className="rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40 disabled:opacity-50"
                >
                  {vaBusy ? 'Syncing…' : 'Sync from ABDP'}
                </button>
              ) : null}
              {canMutateVa && onRunExtract ? (
                <button
                  type="button"
                  disabled={vaBusy || !extractEnabled || autoExtracted}
                  onClick={() => onRunExtract?.()}
                  title={
                    autoExtracted
                      ? 'ABDP (Method 2) already extracted this batch. Use Sync from ABDP.'
                      : extractEnabled
                        ? 'Re-run Digitize extraction'
                        : 'Run classification first so document types sync into Digitize'
                  }
                  className="rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-navy-600 hover:border-eorig-navy-600/40 disabled:opacity-50"
                >
                  {vaBusy ? 'Running…' : autoExtracted ? 'Already auto-extracted' : 'Re-run extraction'}
                </button>
              ) : null}
            </div>
          </div>
          <ExtractionDisclaimer
            extractionState={extractionState}
            fieldTotal={fieldTotal}
            fieldVerified={fieldVerified}
          />
          {!jobs.length ? (
            <p className="px-4 py-8 text-sm text-eorig-muted">
              Classify this loan’s source document first. No extracted fields yet.
            </p>
          ) : (
            <div>
              <FactList
                facts={jobFields}
                jobId={activeJobId}
                canVerify={canMutateVa}
                onVerifyFact={onVerifyFact}
                verifyBusy={verifyBusy}
                canEdit={fieldEditEnabled}
                draftValues={draftValues}
                onDraftChange={handleDraftChange}
                emptyMessage={
                  autoChain
                    ? syncAllowed
                      ? 'No fields for this type yet. Use Sync from ABDP to pull the extraction already waiting in Review.'
                      : 'No fields for this type yet. ABDP extracts automatically — wait for Review, then Sync from ABDP.'
                    : extractEnabled
                      ? 'No fields for this type yet. Run extraction from Classify, then open Review.'
                      : 'Classify first, then run extraction from the Classify tab.'
                }
              />
            </div>
          )}
        </div>
      ) : null}

      {tab === 'review' ? (
        <div className="overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
          <div className="flex flex-wrap items-center gap-3 border-b border-eorig-border px-4 py-3">
            <h3 className="shrink-0 text-sm font-semibold text-eorig-ink">Review</h3>
            <DocumentTypeSelect
              id="review-doc-type"
              jobs={jobs}
              selectedId={activeJobId}
              onSelect={setSelectedJobId}
            />
            {activeJob?.pageCount || activeJob?.startPage ? (
              <p className="text-[11px] text-eorig-muted">
                {activeJob.startPage ? `from p.${activeJob.startPage}` : null}
                {activeJob.startPage && activeJob.pageCount ? ' · ' : null}
                {activeJob.pageCount ? `${activeJob.pageCount} page(s)` : null}
              </p>
            ) : null}
            {canMutateVa && onVerifyDocumentType && jobFields.some((f) => f.verificationStatus !== 'verified') ? (
              <button
                type="button"
                disabled={verifyBusy || !jobFields.length || dirtyFieldIds.length > 0}
                onClick={() => onVerifyDocumentType?.(activeJobId)}
                className="ml-auto rounded-full border border-eorig-navy-600 bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-eorig-navy-700 disabled:opacity-50"
              >
                {verifyBusy ? 'Verifying…' : 'Verify all for type'}
              </button>
            ) : null}
            {saveFieldsButton}
          </div>
          <ExtractionDisclaimer
            extractionState={extractionState}
            fieldTotal={fieldTotal}
            fieldVerified={fieldVerified}
          />
          {!jobs.length ? (
            <p className="px-4 py-8 text-sm text-eorig-muted">
              No classified documents yet. Run classification, then extraction.
            </p>
          ) : (
            <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-eorig-border">
              <div className="flex min-h-0 flex-col">
                <div className="border-b border-eorig-border px-4 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Fields
                  </p>
                </div>
                <div>
                  <FactList
                    facts={jobFields}
                    jobId={activeJobId}
                    canVerify={canMutateVa}
                    onVerifyFact={onVerifyFact}
                    verifyBusy={verifyBusy}
                    canEdit={fieldEditEnabled}
                    draftValues={draftValues}
                    onDraftChange={handleDraftChange}
                    emptyMessage={
                      autoChain
                        ? syncAllowed
                          ? 'No extracted fields for this type yet. Use Sync from ABDP to pull them.'
                          : 'No extracted fields for this type yet. Wait for ABDP Review, then Sync from ABDP.'
                        : 'No extracted fields for this type yet. Run extraction from Classify.'
                    }
                  />
                </div>
              </div>
              <div className="flex min-h-0 flex-col border-t border-eorig-border lg:border-t-0">
                <div className="border-b border-eorig-border px-4 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                    Source pages
                  </p>
                </div>
                <SourceDocumentPane
                  activeDoc={previewDoc?.id ? previewDoc : sourceDoc}
                  loadDocumentPreview={loadDocumentPreview}
                  listDocumentMembers={listDocumentMembers}
                  startPage={activeJob?.startPage}
                  pageCount={activeJob?.pageCount}
                  pages={activeJob?.pages}
                  emptyMessage="Classify first, then select a document type to review pages."
                  labelledCopies={labelledCopies}
                  onDownloadCopy={onDownloadCopy}
                  sourceIsZip={sourceIsZip}
                  jobFileName={activeJob?.fileName || ''}
                  jobCount={jobs.length}
                  abdpReviewUrl={abdpReviewUrl}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'validation' ? (
        <ValidationPanel
          items={validationItems}
          assetId={validationAssetId}
          fetchEvidencePage={fetchValidationPage}
          fetchEvidenceDetails={fetchValidationDetails}
          evidenceReloadToken={validationReloadToken}
          canMutateVa={canMutateVa}
          vaBusy={vaBusy}
          onRunValidate={onRunValidate}
          loanTape={loanTape}
          onRunTapeValidate={onRunTapeValidate}
        />
      ) : null}
    </div>
  );
}
